import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { CalendarClock, Plus, Edit, Trash2, Users, Phone, Armchair, Check, X as XIcon, UserCheck } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { UiSelect } from '../components/UiSelect';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';
import { reservationService, Reservation, ReservationStatus, CreateReservationRequest } from '../services/reservation';
import { tableService } from '../services/table';
import { Modal, Field, ModalActions, Th, inputCls } from './inventory/StockItemsTab';

const STATUS_BADGE: Record<ReservationStatus, string> = {
  BOOKED: 'bg-blue-100 text-blue-800',
  SEATED: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-gray-200 text-gray-600',
};

interface ReservationFormData {
  customerName: string;
  customerMobile?: string;
  partySize?: number;
  reservationTime: string;
  durationMinutes?: number;
  tableId?: string;
  notes?: string;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

function fmtTime(t?: string) {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return t;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export const Reservations: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;

  const [date, setDate] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | ''>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Reservation | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; id: string; label: string }>({ open: false, id: '', label: '' });

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<ReservationFormData>();

  const { data: reservations, isLoading } = useQuery(
    ['reservations', org, restaurantId, date, statusFilter],
    () => reservationService.list(org!, restaurantId, date, statusFilter || undefined),
    { enabled: !!org },
  );

  const { data: tables } = useQuery(
    ['tables-for-reservation', org, restaurantId],
    () => tableService.getTables({ org: org!, restaurantId }),
    { enabled: !!org },
  );

  const createMutation = useMutation(reservationService.create, {
    onSuccess: () => { queryClient.invalidateQueries(['reservations']); close(); toast.success('Reservation booked'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to book reservation'); },
  });
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateReservationRequest> }) => reservationService.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['reservations']); close(); toast.success('Reservation updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update reservation'); },
    },
  );
  const statusMutation = useMutation(
    ({ id, status }: { id: string; status: ReservationStatus }) => reservationService.setStatus(id, status),
    {
      onSuccess: () => { queryClient.invalidateQueries(['reservations']); toast.success('Status updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update status'); },
    },
  );
  const deleteMutation = useMutation(reservationService.remove, {
    onSuccess: () => { queryClient.invalidateQueries(['reservations']); toast.success('Reservation deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete reservation'); },
  });

  const counts = useMemo(() => {
    const list = reservations ?? [];
    return {
      total: list.length,
      booked: list.filter(r => r.status === 'BOOKED').length,
      seated: list.filter(r => r.status === 'SEATED').length,
    };
  }, [reservations]);

  function close() { setIsFormOpen(false); setEditing(null); reset(); }
  function openCreate() {
    setEditing(null);
    reset({ reservationTime: `${date}T19:00`, partySize: 2, durationMinutes: 90 });
    setIsFormOpen(true);
  }
  function openEdit(r: Reservation) {
    setEditing(r);
    reset({ customerName: r.customerName, customerMobile: r.customerMobile, partySize: r.partySize, reservationTime: r.reservationTime, durationMinutes: r.durationMinutes, tableId: r.tableId, notes: r.notes });
    setIsFormOpen(true);
  }

  const onSubmit = (data: ReservationFormData) => {
    const payload: CreateReservationRequest = {
      ...data,
      partySize: data.partySize ? Number(data.partySize) : undefined,
      durationMinutes: data.durationMinutes ? Number(data.durationMinutes) : undefined,
      tableId: data.tableId || undefined,
      organizationCode: org!,
      ...(restaurantId && { restaurantId }),
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  if (!org) {
    return <div className="p-6"><EmptyState icon={<CalendarClock className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." /></div>;
  }

  const statusActions: { status: ReservationStatus; label: string; icon: React.ReactNode; color: string; bg: string }[] = [
    { status: 'SEATED', label: 'Seat', icon: <UserCheck className="h-4 w-4" />, color: '#4338ca', bg: '#eef2ff' },
    { status: 'COMPLETED', label: 'Done', icon: <Check className="h-4 w-4" />, color: '#16a34a', bg: '#f0fdf4' },
    { status: 'NO_SHOW', label: 'No-show', icon: <XIcon className="h-4 w-4" />, color: '#6b7280', bg: '#f3f4f6' },
    { status: 'CANCELLED', label: 'Cancel', icon: <XIcon className="h-4 w-4" />, color: '#dc2626', bg: '#fef2f2' },
  ];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <CalendarClock className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Reservations</h1>
            <p style={{ color: currentTheme.colors.text.secondary }}>
              {counts.total} booking{counts.total === 1 ? '' : 's'} · {counts.booked} upcoming · {counts.seated} seated
            </p>
          </div>
        </div>
        <button onClick={openCreate} className="mt-3 sm:mt-0 btn-primary">
          <Plus className="h-5 w-5" /> New Reservation
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="ui-input" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>Status</label>
          <UiSelect
            value={statusFilter}
            onChange={v => setStatusFilter(v as ReservationStatus | '')}
            options={[{ value: '', label: 'All' }, ...(['BOOKED', 'SEATED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'] as ReservationStatus[]).map(s => ({ value: s, label: s }))]}
            className="w-44"
          />
        </div>
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={5} />
      ) : !reservations || reservations.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No reservations"
          description="No bookings for this date. Create one to reserve a table."
          action={<button onClick={openCreate} className="btn-primary"><Plus className="h-5 w-5" /> New Reservation</button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Time</Th><Th>Guest</Th><Th>Party</Th><Th>Table</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {reservations.map(r => (
                <tr key={r.id}>
                  <td className="px-4 py-3 text-sm font-medium whitespace-nowrap" style={{ color: currentTheme.colors.text.primary }}>{fmtTime(r.reservationTime)}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>
                    <div className="font-medium">{r.customerName}</div>
                    {r.customerMobile && <div className="text-xs flex items-center gap-1" style={{ color: currentTheme.colors.text.secondary }}><Phone className="h-3 w-3" /> {r.customerMobile}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {r.partySize ?? '—'}</span></td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{r.tableName ? <span className="inline-flex items-center gap-1"><Armchair className="h-3.5 w-3.5" /> {r.tableName}</span> : '—'}</td>
                  <td className="px-4 py-3"><span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_BADGE[r.status])}>{r.status.replace('_', ' ')}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {(r.status === 'BOOKED' || r.status === 'SEATED') && statusActions
                        .filter(a => !(r.status === 'SEATED' && a.status === 'SEATED'))
                        .map(a => (
                          <button key={a.status} onClick={() => statusMutation.mutate({ id: r.id, status: a.status })} title={a.label}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium" style={{ color: a.color, backgroundColor: a.bg }}>
                            {a.icon}<span className="hidden sm:inline">{a.label}</span>
                          </button>
                        ))}
                      <button onClick={() => openEdit(r)} className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }}><Edit className="h-4 w-4" /></button>
                      <button onClick={() => setConfirm({ open: true, id: r.id, label: `${r.customerName} · ${fmtTime(r.reservationTime)}` })} className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <Modal title={editing ? 'Edit Reservation' : 'New Reservation'} onClose={close} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Guest Name" error={errors.customerName?.message} required>
                <input {...register('customerName', { required: 'Name is required' })} className={inputCls} placeholder="e.g., Rahul Sharma" />
              </Field>
              <Field label="Mobile"><input {...register('customerMobile')} className={inputCls} placeholder="optional" /></Field>
              <Field label="Date & Time" error={errors.reservationTime?.message} required>
                <input type="datetime-local" {...register('reservationTime', { required: 'Time is required' })} className={inputCls} />
              </Field>
              <Field label="Party Size"><input type="number" min="1" {...register('partySize')} className={inputCls} placeholder="2" /></Field>
              <Field label="Duration (minutes)"><input type="number" min="0" step="15" {...register('durationMinutes')} className={inputCls} placeholder="90" /></Field>
              <Field label="Table">
                <Controller name="tableId" control={control} render={({ field }) => (
                  <UiSelect value={field.value || ''} onChange={field.onChange} placeholder="Unassigned"
                    options={[{ value: '', label: 'Unassigned' }, ...(tables || []).filter(t => t.active).map(t => ({ value: t.id, label: `${t.name}${t.capacity ? ` (${t.capacity})` : ''}` }))]} />
                )} />
              </Field>
            </div>
            <Field label="Notes"><textarea {...register('notes')} rows={2} className={inputCls} placeholder="e.g., birthday, window seat" /></Field>
            <ModalActions onCancel={close} loading={createMutation.isLoading || updateMutation.isLoading}
              submitLabel={editing ? 'Update' : 'Book'} loadingLabel={editing ? 'Updating...' : 'Booking...'} />
          </form>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={() => { deleteMutation.mutate(confirm.id); setConfirm(c => ({ ...c, open: false })); }}
        title="Delete Reservation" message={`Delete reservation for "${confirm.label}"?`}
        confirmText="Delete" type="danger" isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};
